import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Textarea } from './ui/textarea';
import { Switch } from './ui/switch';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from './ui/dialog';
import { KeyRound, Plus, Copy, Trash2, RefreshCw, Loader2, ShieldCheck, AlertTriangle, Link2 } from 'lucide-react';
import { toast } from 'sonner';

const FRONTEND_ORIGIN = window.location.origin;

const copy = (text) => {
  navigator.clipboard?.writeText(text);
  toast.success('Copied to clipboard');
};

/** One-time secret reveal dialog (shown after create / rotate). */
const SecretRevealDialog = ({ open, onClose, data }) => (
  <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
    <DialogContent data-testid="sso-secret-dialog">
      <DialogHeader>
        <DialogTitle>Save these credentials now</DialogTitle>
        <DialogDescription>
          The client secret is shown <strong>only once</strong>. Store it securely in the app's
          backend — you can rotate it later, but it can't be viewed again.
        </DialogDescription>
      </DialogHeader>
      {data && (
        <div className="space-y-3">
          <div className="space-y-1">
            <Label className="text-xs text-zinc-500">Client ID</Label>
            <div className="flex gap-2">
              <Input readOnly value={data.client_id} className="font-mono text-xs" data-testid="reveal-client-id" />
              <Button size="icon" variant="outline" onClick={() => copy(data.client_id)}><Copy className="w-4 h-4" /></Button>
            </div>
          </div>
          <div className="space-y-1">
            <Label className="text-xs text-zinc-500">Client Secret</Label>
            <div className="flex gap-2">
              <Input readOnly value={data.client_secret} className="font-mono text-xs" data-testid="reveal-client-secret" />
              <Button size="icon" variant="outline" onClick={() => copy(data.client_secret)}><Copy className="w-4 h-4" /></Button>
            </div>
          </div>
        </div>
      )}
      <DialogFooter>
        <Button onClick={onClose} data-testid="sso-secret-done-btn">I've saved them</Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
);

export const SSOApplicationsSection = () => {
  const { api } = useAuth();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState({ name: '', description: '', logo_url: '', home_url: '', redirect_uris: '' });
  const [saving, setSaving] = useState(false);
  const [reveal, setReveal] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/sso/admin/clients');
      setClients(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load SSO applications');
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => { load(); }, [load]);

  const handleCreate = async () => {
    if (!form.name.trim()) { toast.error('Application name is required'); return; }
    setSaving(true);
    try {
      const redirect_uris = form.redirect_uris.split('\n').map((s) => s.trim()).filter(Boolean);
      const { data } = await api.post('/sso/admin/clients', {
        name: form.name, description: form.description, logo_url: form.logo_url, home_url: form.home_url, redirect_uris,
      });
      setReveal({ client_id: data.client_id, client_secret: data.client_secret });
      setCreateOpen(false);
      setForm({ name: '', description: '', logo_url: '', home_url: '', redirect_uris: '' });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to create application');
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (client) => {
    try {
      await api.patch(`/sso/admin/clients/${client.client_id}`, { active: !client.active });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update');
    }
  };

  const rotate = async (client) => {
    if (!window.confirm(`Rotate the secret for "${client.name}"? Existing sessions using the old secret will break until updated.`)) return;
    try {
      const { data } = await api.post(`/sso/admin/clients/${client.client_id}/rotate-secret`);
      setReveal({ client_id: data.client_id, client_secret: data.client_secret });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to rotate secret');
    }
  };

  const remove = async (client) => {
    if (!window.confirm(`Delete "${client.name}"? This immediately revokes SSO for that app.`)) return;
    try {
      await api.delete(`/sso/admin/clients/${client.client_id}`);
      toast.success('Application deleted');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to delete');
    }
  };

  return (
    <div className="space-y-6" data-testid="sso-applications-section">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h2 className="text-xl font-bold flex items-center gap-2">
            <KeyRound className="w-5 h-5 text-indigo-600" /> SSO Applications
          </h2>
          <p className="text-sm text-zinc-500 mt-1 max-w-2xl">
            Register sibling apps (Learnorcreate, Salescrmcoach…) so users can sign in once with
            their PostToSocial identity. Each app gets a client ID/secret and uses the hosted login at{' '}
            <code className="text-xs bg-zinc-100 px-1.5 py-0.5 rounded">{FRONTEND_ORIGIN}/sso/login</code>.
          </p>
        </div>
        <Button onClick={() => setCreateOpen(true)} className="bg-indigo-600 hover:bg-indigo-700" data-testid="sso-add-app-btn">
          <Plus className="w-4 h-4 mr-2" /> Register App
        </Button>
      </div>

      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-indigo-600" /></div>
      ) : clients.length === 0 ? (
        <Card className="border-dashed">
          <CardContent className="py-12 text-center text-zinc-500">
            <ShieldCheck className="w-10 h-10 mx-auto mb-3 text-zinc-300" />
            <p className="font-medium text-zinc-700">No SSO applications yet</p>
            <p className="text-sm">Register your first app to enable single sign-on across the bundle.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4">
          {clients.map((c) => (
            <Card key={c.client_id} data-testid={`sso-app-${c.client_id}`}>
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-center gap-3">
                    {c.logo_url ? (
                      <img src={c.logo_url} alt="" className="w-9 h-9 rounded-lg object-cover border" />
                    ) : (
                      <div className="w-9 h-9 rounded-lg bg-indigo-50 flex items-center justify-center">
                        <Link2 className="w-4 h-4 text-indigo-600" />
                      </div>
                    )}
                    <div>
                      <CardTitle className="text-base flex items-center gap-2">
                        {c.name}
                        {c.active
                          ? <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100">Active</Badge>
                          : <Badge variant="secondary">Disabled</Badge>}
                      </CardTitle>
                      {c.description && <CardDescription className="mt-0.5">{c.description}</CardDescription>}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Switch checked={c.active} onCheckedChange={() => toggleActive(c)} data-testid={`sso-toggle-${c.client_id}`} />
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                {c.home_url && (
                  <div className="space-y-1">
                    <Label className="text-xs text-zinc-500">Launch URL (App Switcher)</Label>
                    <a href={c.home_url} target="_blank" rel="noreferrer" className="text-xs text-indigo-600 hover:underline break-all block">{c.home_url}</a>
                  </div>
                )}
                <div className="space-y-1">
                  <Label className="text-xs text-zinc-500">Client ID</Label>
                  <div className="flex gap-2">
                    <Input readOnly value={c.client_id} className="font-mono text-xs h-8" />
                    <Button size="icon" variant="outline" className="h-8 w-8" onClick={() => copy(c.client_id)}><Copy className="w-3.5 h-3.5" /></Button>
                  </div>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs text-zinc-500">Redirect URIs ({c.redirect_uris?.length || 0})</Label>
                  {c.redirect_uris?.length ? (
                    <div className="flex flex-wrap gap-1.5">
                      {c.redirect_uris.map((u) => (
                        <code key={u} className="text-[11px] bg-zinc-100 px-2 py-1 rounded break-all">{u}</code>
                      ))}
                    </div>
                  ) : (
                    <p className="text-xs text-amber-600 flex items-center gap-1">
                      <AlertTriangle className="w-3 h-3" /> No redirect URIs — sign-in will be rejected.
                    </p>
                  )}
                </div>
                <div className="flex gap-2 pt-1">
                  <Button size="sm" variant="outline" onClick={() => rotate(c)} data-testid={`sso-rotate-${c.client_id}`}>
                    <RefreshCw className="w-3.5 h-3.5 mr-1.5" /> Rotate secret
                  </Button>
                  <Button size="sm" variant="ghost" className="text-red-600 hover:text-red-700 hover:bg-red-50" onClick={() => remove(c)} data-testid={`sso-delete-${c.client_id}`}>
                    <Trash2 className="w-3.5 h-3.5 mr-1.5" /> Delete
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Create dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent data-testid="sso-create-dialog">
          <DialogHeader>
            <DialogTitle>Register SSO Application</DialogTitle>
            <DialogDescription>The client secret is generated and shown once after saving.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label>Application name *</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Learnorcreate" data-testid="sso-form-name" />
            </div>
            <div className="space-y-1.5">
              <Label>Description</Label>
              <Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Course creation platform" data-testid="sso-form-description" />
            </div>
            <div className="space-y-1.5">
              <Label>Logo URL</Label>
              <Input value={form.logo_url} onChange={(e) => setForm({ ...form, logo_url: e.target.value })} placeholder="https://…/logo.png" data-testid="sso-form-logo" />
            </div>
            <div className="space-y-1.5">
              <Label>Launch URL (shown in the App Switcher)</Label>
              <Input value={form.home_url} onChange={(e) => setForm({ ...form, home_url: e.target.value })} placeholder="https://learnorcreate.com" data-testid="sso-form-home" />
              <p className="text-[11px] text-zinc-400">Where users land when they pick this app from the switcher. Leave blank to hide it from the switcher.</p>
            </div>
            <div className="space-y-1.5">
              <Label>Redirect URIs (one per line)</Label>
              <Textarea
                rows={3}
                value={form.redirect_uris}
                onChange={(e) => setForm({ ...form, redirect_uris: e.target.value })}
                placeholder={"https://learnorcreate.com/auth/sso/callback\nhttp://localhost:3000/auth/sso/callback"}
                className="font-mono text-xs"
                data-testid="sso-form-redirects"
              />
              <p className="text-[11px] text-zinc-400">Must match exactly what the app sends. Add both production and local URLs.</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)}>Cancel</Button>
            <Button onClick={handleCreate} disabled={saving} className="bg-indigo-600 hover:bg-indigo-700" data-testid="sso-form-submit">
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Plus className="w-4 h-4 mr-2" />}
              Create
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <SecretRevealDialog open={!!reveal} data={reveal} onClose={() => setReveal(null)} />
    </div>
  );
};
